import { CalendarDays, CalendarRange } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  eventoDisplayMeta,
  FERIAS_SITUACAO_META,
  formatPtDay,
  listFeriasEventosProgramados,
} from '@/components/Gestores/gestorEquipeFeriasCalendarUtils'
import { FERIAS_STATUS_LABELS } from '@/components/Gestores/gestorEquipeFeriasUtils'
import type { GestorEquipeFeriasCalendario } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

type Props = {
  data: GestorEquipeFeriasCalendario
  onSelectMember: (id: string) => void
}

export function GestorEquipeFeriasCalendarAside({ data, onSelectMember }: Props) {
  const programadas = listFeriasEventosProgramados(data.eventos)
  const elegiveis = data.membros.filter((m) => m.situacao === 'elegivel')
  const atrasadas = data.membros.filter((m) => m.situacao === 'atrasada')
  const pendentes = data.membros.filter((m) => m.situacao === 'pendente')

  return (
    <aside className='space-y-4'>
      <section className='glass-card p-4'>
        <div className='mb-3 flex items-center gap-2'>
          <CalendarRange size={16} className='text-violet-400' />
          <h3 className='text-sm font-semibold'>Férias programadas</h3>
          <span className='ml-auto rounded-full bg-white/[0.06] px-2 py-0.5 text-xs text-text-muted'>
            {programadas.length}
          </span>
        </div>
        {programadas.length === 0 ? (
          <p className='text-xs text-text-muted'>
            Nenhuma férias ou solicitação com período futuro no momento.
          </p>
        ) : (
          <ul className='space-y-2'>
            {programadas.map((evento) => {
              const meta = eventoDisplayMeta(evento)
              return (
                <li key={evento.id}>
                  <button
                    type='button'
                    onClick={() => onSelectMember(evento.pjAgradaId)}
                    className='w-full rounded-lg border border-violet-500/20 bg-violet-500/[0.06] px-3 py-2 text-left hover:border-violet-400/40'
                  >
                    <div className='flex flex-wrap items-center justify-between gap-2'>
                      <p className='text-sm font-medium'>{evento.nomeCompleto}</p>
                      <span
                        className={cn(
                          'rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase',
                          meta.className,
                        )}
                      >
                        {meta.label}
                      </span>
                    </div>
                    <p className='mt-1 text-xs text-text-muted'>
                      {formatPtDay(new Date(evento.inicio))} → {formatPtDay(new Date(evento.fim))}
                      {evento.dias != null ? ` · ${evento.dias} dia(s)` : ''}
                    </p>
                    <p className='mt-1 text-[11px] text-text-muted'>
                      {evento.tipo === 'ferias_registrada'
                        ? (FERIAS_STATUS_LABELS[evento.status] ?? evento.status)
                        : evento.protocol ?? 'Chamado aberto'}
                    </p>
                    {evento.requestId && (
                      <Link
                        to={`/gestores/chamados/${evento.requestId}`}
                        onClick={(e) => e.stopPropagation()}
                        className='mt-1 inline-block text-xs font-medium text-accent hover:underline'
                      >
                        {evento.protocol}
                      </Link>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <ResumoGrupo
        title='Pode pedir férias'
        icon={<CalendarDays size={16} className='text-emerald-400' />}
        membros={elegiveis}
        onSelect={onSelectMember}
      />
      <ResumoGrupo
        title='Férias atrasadas'
        icon={<CalendarDays size={16} className='text-amber-400' />}
        membros={atrasadas}
        onSelect={onSelectMember}
        emptyHint='Ninguém com férias atrasadas no momento.'
      />
      <ResumoGrupo
        title='Aguardando elegibilidade'
        icon={<CalendarDays size={16} className='text-sky-400' />}
        membros={pendentes}
        onSelect={onSelectMember}
        emptyHint='Todos elegíveis ou já em férias.'
      />
    </aside>
  )
}

function ResumoGrupo({
  title,
  icon,
  membros,
  onSelect,
  emptyHint,
}: {
  title: string
  icon: React.ReactNode
  membros: GestorEquipeFeriasCalendario['membros']
  onSelect: (id: string) => void
  emptyHint?: string
}) {
  return (
    <section className='glass-card p-4'>
      <div className='mb-3 flex items-center gap-2'>
        {icon}
        <h3 className='text-sm font-semibold'>{title}</h3>
        <span className='ml-auto rounded-full bg-white/[0.06] px-2 py-0.5 text-xs text-text-muted'>
          {membros.length}
        </span>
      </div>
      {membros.length === 0 ? (
        <p className='text-xs text-text-muted'>{emptyHint ?? 'Nenhum profissional nesta lista.'}</p>
      ) : (
        <ul className='space-y-2'>
          {membros.map((membro) => {
            const meta = FERIAS_SITUACAO_META[membro.situacao]
            return (
              <li key={membro.id}>
                <button
                  type='button'
                  onClick={() => onSelect(membro.id)}
                  className='w-full rounded-lg border border-border px-3 py-2 text-left hover:border-accent/30'
                >
                  <p className='text-sm font-medium'>{membro.nomeCompleto}</p>
                  <p className='mt-1 text-xs text-text-muted'>
                    Saldo {membro.feriasResumo.diasRestantes}/20 · elegível desde{' '}
                    {membro.feriasResumo.elegivelDesde
                      ? formatPtDay(new Date(membro.feriasResumo.elegivelDesde))
                      : '—'}
                  </p>
                  <span className={cn('mt-2 inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold', meta.tone)}>
                    {meta.label}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
